import { useRef } from "react";
import RichTextEditor from "@/components/RichTextEditor";
import { Plus, Trash2, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * Per-page structured CMS editors. Each editor receives initial { title, content }
 * and exposes onChange({ title, content }) calls so the parent can save when ready.
 *
 * Array sections use a real drag-and-drop SortableList (dnd-kit) so admins can
 * reorder items by dragging the handle.
 */

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] block mb-2">{label}</label>
      {children}
      {hint && <div className="text-xs text-[var(--dojo-ink-soft)] mt-1.5">{hint}</div>}
    </div>
  );
}

function ArrayHeader({ label, onAdd, count }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">
        {label} · {count} <span className="normal-case tracking-normal">· drag to reorder</span>
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-1 px-3 py-1 border border-[var(--dojo-ink)] text-xs uppercase tracking-widest hover:bg-[var(--dojo-ink)] hover:text-white transition-colors"
        data-testid={`rte-add-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Plus size={12} /> Add
      </button>
    </div>
  );
}

// -------------------------------------------------------- SORTABLE PRIMITIVE --
function SortableRow({ id, index, onRemove, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 20 : "auto",
    boxShadow: isDragging ? "0 12px 28px -8px rgba(0,0,0,0.35)" : "none",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] p-4 relative"
      data-testid={`sortable-row-${index}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          className="flex flex-col items-center text-[var(--dojo-ink-soft)] pt-1 cursor-grab active:cursor-grabbing touch-none select-none"
          {...attributes}
          {...listeners}
          data-testid={`row-drag-${index}`}
          aria-label="Drag to reorder"
          title="Drag to reorder"
        >
          <GripVertical size={16} />
          <span className="text-[10px] mt-1">{index + 1}</span>
        </button>
        <div className="flex-1 space-y-3">{children}</div>
        <button
          type="button"
          onClick={onRemove}
          className="text-[var(--dojo-hinomaru)] hover:text-[var(--dojo-hinomaru-dark)]"
          data-testid={`row-remove-${index}`}
          aria-label="Remove item"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * Generic drag-and-drop list. `items` is an array of plain objects; `onChange`
 * receives the reordered/edited array. Stable row ids are held in a ref so they
 * travel with their item across reorders (keeping input focus stable).
 */
function SortableList({ items, onChange, renderRow, emptyText }) {
  const idsRef = useRef([]);
  if (idsRef.current.length !== items.length) {
    // Length changed (add/remove) — rebuild ids, reusing existing where possible.
    idsRef.current = items.map((_, i) => idsRef.current[i] || `row-${Math.random().toString(36).slice(2, 9)}`);
  }
  const ids = idsRef.current;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;
    idsRef.current = arrayMove(ids, from, to);
    onChange(arrayMove(items, from, to));
  };

  const remove = (i) => {
    idsRef.current = ids.filter((_, idx) => idx !== i);
    onChange(items.filter((_, idx) => idx !== i));
  };

  if (items.length === 0) {
    return <div className="text-sm text-[var(--dojo-ink-soft)]">{emptyText || "Nothing yet. Click Add."}</div>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-3">
          {items.map((item, i) => (
            <SortableRow key={ids[i]} id={ids[i]} index={i} onRemove={() => remove(i)}>
              {renderRow(item, i)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

// A small image-URL field with live preview.
function ImageField({ label, value, onChange, testid, hint }) {
  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-3">
        {value && (
          <img
            src={value}
            alt="preview"
            referrerPolicy="no-referrer"
            className="h-12 w-12 object-cover border border-[var(--dojo-border)]"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <input className="input" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…" data-testid={testid} />
      </div>
    </Field>
  );
}

// Image field that supports BOTH direct upload (stored inline as a base64 data
// URL, ≤1.5 MB) and pasting an external URL.
function ImageUploadField({ value, onChange, testid }) {
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { alert("Image must be under 1.5 MB. Please compress it first."); return; }
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {value && (
        <img
          src={value}
          alt="preview"
          referrerPolicy="no-referrer"
          className="h-12 w-12 object-cover border border-[var(--dojo-border)]"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      <input className="input flex-1 min-w-[160px]" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload →" data-testid={testid} />
      <label className="btn-outline text-xs cursor-pointer whitespace-nowrap">
        Upload
        <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={testid ? `${testid}-file` : undefined} />
      </label>
      {value && (
        <button type="button" onClick={() => onChange("")} className="text-xs text-[var(--dojo-hinomaru)] underline">Remove</button>
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      {label}
    </label>
  );
}

// -------------------------------------------------------------------- HOME --
export function HomeEditor({ value, onChange, title, onTitleChange }) {
  const c = value || {};
  const set = (k, v) => onChange({ ...c, [k]: v });
  const mem = c.memorial || {};
  const setMem = (k, v) => set("memorial", { ...mem, [k]: v });

  const gallery = c.gallery || [];
  const instructors = c.instructors || [];
  const testimonials = c.testimonials || [];
  const upd = (list, i, patch) => list.map((it, idx) => (idx === i ? { ...it, ...patch } : it));

  return (
    <div className="space-y-6">
      <Field label="Page Title (admin label)">
        <input className="input" value={title} onChange={(e) => onTitleChange(e.target.value)} data-testid="cms-title" />
      </Field>

      {/* Memorial banner */}
      <div className="border border-[var(--dojo-border)] p-4 space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={!!mem.enabled} onChange={(e) => setMem("enabled", e.target.checked)} data-testid="cms-memorial-enabled" />
          Show memorial banner
        </label>
        {mem.enabled && (
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Prefix"><input className="input" value={mem.prefix || ""} onChange={(e) => setMem("prefix", e.target.value)} /></Field>
            <Field label="Name"><input className="input" value={mem.name || ""} onChange={(e) => setMem("name", e.target.value)} /></Field>
            <div className="md:col-span-2"><Field label="Message"><textarea className="input" rows={2} value={mem.message || ""} onChange={(e) => setMem("message", e.target.value)} /></Field></div>
            <Field label="Button label"><input className="input" value={mem.cta_label || ""} onChange={(e) => setMem("cta_label", e.target.value)} /></Field>
            <Field label="Button URL"><input className="input" value={mem.cta_url || ""} onChange={(e) => setMem("cta_url", e.target.value)} /></Field>
            <div className="md:col-span-2"><Field label="Button note"><input className="input" value={mem.cta_note || ""} onChange={(e) => setMem("cta_note", e.target.value)} /></Field></div>
          </div>
        )}
      </div>

      {/* Section visibility toggles */}
      <div className="border border-[var(--dojo-border)] p-4">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] mb-3">Visible sections</div>
        <div className="flex flex-wrap gap-6">
          <Toggle label="Gallery" checked={c.show_gallery !== false} onChange={(v) => set("show_gallery", v)} testid="cms-toggle-gallery" />
          <Toggle label="Instructors" checked={c.show_instructors !== false} onChange={(v) => set("show_instructors", v)} testid="cms-toggle-instructors" />
          <Toggle label="Testimonials" checked={c.show_testimonials !== false} onChange={(v) => set("show_testimonials", v)} testid="cms-toggle-testimonials" />
        </div>
      </div>

      {/* Hero */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Tagline"><input className="input" value={c.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="cms-tagline" /></Field>
        <Field label="Kanji (right of hero)"><input className="input" value={c.kanji || ""} onChange={(e) => set("kanji", e.target.value)} data-testid="cms-kanji" /></Field>
      </div>
      <Field label="Hero Headline"><input className="input" value={c.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} data-testid="cms-hero-headline" /></Field>
      <Field label="Hero Sub-headline"><textarea className="input" rows={3} value={c.hero_sub || ""} onChange={(e) => set("hero_sub", e.target.value)} data-testid="cms-hero-sub" /></Field>
      <ImageField label="Hero background image URL" value={c.hero_image} onChange={(v) => set("hero_image", v)} testid="cms-hero-image" />

      {/* Welcome + About */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Welcome title"><input className="input" value={c.welcome_title || ""} onChange={(e) => set("welcome_title", e.target.value)} /></Field>
        <Field label="Welcome subtitle"><input className="input" value={c.welcome_sub || ""} onChange={(e) => set("welcome_sub", e.target.value)} /></Field>
      </div>
      <ImageField label="Welcome image URL" value={c.welcome_image} onChange={(v) => set("welcome_image", v)} />
      <Field label="About heading"><input className="input" value={c.about_heading || ""} onChange={(e) => set("about_heading", e.target.value)} /></Field>
      <Field label="About body"><textarea className="input" rows={4} value={c.about_body || ""} onChange={(e) => set("about_body", e.target.value)} data-testid="cms-about-body" /></Field>

      {/* Experience strip */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Experience title"><input className="input" value={c.experience_title || ""} onChange={(e) => set("experience_title", e.target.value)} /></Field>
        <Field label="Experience eyebrow"><input className="input" value={c.experience_sub || ""} onChange={(e) => set("experience_sub", e.target.value)} /></Field>
      </div>
      <ImageField label="Experience image URL" value={c.experience_image} onChange={(v) => set("experience_image", v)} />

      {/* Gallery */}
      <div>
        <Field label="Gallery heading"><input className="input" value={c.gallery_heading || ""} onChange={(e) => set("gallery_heading", e.target.value)} /></Field>
        <div className="mt-3">
          <ArrayHeader label="Gallery" count={gallery.length} onAdd={() => set("gallery", [...gallery, { url: "", caption: "" }])} />
          <SortableList
            items={gallery}
            onChange={(next) => set("gallery", next)}
            emptyText="No gallery images yet. Click Add."
            renderRow={(g, i) => (
              <>
                <ImageUploadField value={g.url} onChange={(v) => set("gallery", upd(gallery, i, { url: v }))} testid={`cms-gallery-url-${i}`} />
                <input className="input" value={g.caption || ""} onChange={(e) => set("gallery", upd(gallery, i, { caption: e.target.value }))} placeholder="Caption" />
              </>
            )}
          />
        </div>
      </div>

      {/* Instructors */}
      <div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Instructors eyebrow"><input className="input" value={c.instructors_eyebrow || ""} onChange={(e) => set("instructors_eyebrow", e.target.value)} /></Field>
          <Field label="Instructors heading"><input className="input" value={c.instructors_heading || ""} onChange={(e) => set("instructors_heading", e.target.value)} /></Field>
        </div>
        <div className="mt-3">
          <ArrayHeader label="Instructors" count={instructors.length} onAdd={() => set("instructors", [...instructors, { name: "New Instructor", location: "", phone: "", image: "" }])} />
          <SortableList
            items={instructors}
            onChange={(next) => set("instructors", next)}
            emptyText="No instructors yet. Click Add."
            renderRow={(ins, i) => (
              <>
                <input className="input" value={ins.name || ""} onChange={(e) => set("instructors", upd(instructors, i, { name: e.target.value }))} placeholder="Name" data-testid={`cms-instructor-name-${i}`} />
                <div className="grid md:grid-cols-2 gap-3">
                  <input className="input" value={ins.location || ""} onChange={(e) => set("instructors", upd(instructors, i, { location: e.target.value }))} placeholder="Location" />
                  <input className="input" value={ins.phone || ""} onChange={(e) => set("instructors", upd(instructors, i, { phone: e.target.value }))} placeholder="Phone" />
                </div>
                <ImageUploadField value={ins.image} onChange={(v) => set("instructors", upd(instructors, i, { image: v }))} testid={`cms-instructor-image-${i}`} />
              </>
            )}
          />
        </div>
      </div>

      {/* Testimonials */}
      <div>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Reviews heading"><input className="input" value={c.reviews_heading || ""} onChange={(e) => set("reviews_heading", e.target.value)} /></Field>
          <Field label="Rating"><input className="input" value={c.reviews_rating || ""} onChange={(e) => set("reviews_rating", e.target.value)} /></Field>
          <Field label="Count label"><input className="input" value={c.reviews_count || ""} onChange={(e) => set("reviews_count", e.target.value)} /></Field>
        </div>
        <div className="mt-3">
          <ArrayHeader label="Testimonials" count={testimonials.length} onAdd={() => set("testimonials", [...testimonials, { name: "New Reviewer", when: "", text: "" }])} />
          <SortableList
            items={testimonials}
            onChange={(next) => set("testimonials", next)}
            emptyText="No testimonials yet. Click Add."
            renderRow={(tm, i) => (
              <>
                <div className="grid md:grid-cols-2 gap-3">
                  <input className="input" value={tm.name || ""} onChange={(e) => set("testimonials", upd(testimonials, i, { name: e.target.value }))} placeholder="Reviewer name" data-testid={`cms-testimonial-name-${i}`} />
                  <input className="input" value={tm.when || ""} onChange={(e) => set("testimonials", upd(testimonials, i, { when: e.target.value }))} placeholder="When (e.g. 2 years ago)" />
                </div>
                <textarea className="input" rows={2} value={tm.text || ""} onChange={(e) => set("testimonials", upd(testimonials, i, { text: e.target.value }))} placeholder="Review text" />
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- ABOUT --
export function AboutEditor({ value, onChange, title, onTitleChange }) {
  const c = value || {};
  const set = (k, v) => onChange({ ...c, [k]: v });
  const values = c.values || [];
  const instructors = c.instructors || [];
  const upd = (list, i, patch) => list.map((it, idx) => (idx === i ? { ...it, ...patch } : it));

  return (
    <div className="space-y-6">
      <Field label="Page Title">
        <input className="input" value={title} onChange={(e) => onTitleChange(e.target.value)} data-testid="cms-title" />
      </Field>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Eyebrow"><input className="input" value={c.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></Field>
        <Field label="Heading"><input className="input" value={c.heading || ""} onChange={(e) => set("heading", e.target.value)} data-testid="cms-about-heading" /></Field>
      </div>
      <ImageField label="About image URL" value={c.image} onChange={(v) => set("image", v)} />
      <Field label="Body"><textarea className="input" rows={5} value={c.body || ""} onChange={(e) => set("body", e.target.value)} data-testid="cms-about-body" /></Field>
      <Field label="Quote"><textarea className="input" rows={2} value={c.quote || ""} onChange={(e) => set("quote", e.target.value)} /></Field>

      {/* Values */}
      <div>
        <ArrayHeader label="Values" count={values.length} onAdd={() => set("values", [...values, { title: "New Value", desc: "" }])} />
        <SortableList
          items={values}
          onChange={(next) => set("values", next)}
          emptyText="No values yet. Click Add."
          renderRow={(v, i) => (
            <>
              <input className="input" value={v.title || ""} onChange={(e) => set("values", upd(values, i, { title: e.target.value }))} placeholder="Value title" data-testid={`cms-value-title-${i}`} />
              <textarea className="input" rows={2} value={v.desc || ""} onChange={(e) => set("values", upd(values, i, { desc: e.target.value }))} placeholder="Description" />
            </>
          )}
        />
      </div>

      {/* Instructors */}
      <div>
        <Field label="Instructors heading"><input className="input" value={c.instructors_heading || ""} onChange={(e) => set("instructors_heading", e.target.value)} /></Field>
        <div className="mt-3">
          <ArrayHeader label="Instructors" count={instructors.length} onAdd={() => set("instructors", [...instructors, { name: "New Instructor", location: "", phone: "", image: "" }])} />
          <SortableList
            items={instructors}
            onChange={(next) => set("instructors", next)}
            emptyText="No instructors yet. Click Add."
            renderRow={(ins, i) => (
              <>
                <input className="input" value={ins.name || ""} onChange={(e) => set("instructors", upd(instructors, i, { name: e.target.value }))} placeholder="Name" />
                <div className="grid md:grid-cols-2 gap-3">
                  <input className="input" value={ins.location || ""} onChange={(e) => set("instructors", upd(instructors, i, { location: e.target.value }))} placeholder="Location" />
                  <input className="input" value={ins.phone || ""} onChange={(e) => set("instructors", upd(instructors, i, { phone: e.target.value }))} placeholder="Phone" />
                </div>
                <ImageUploadField value={ins.image} onChange={(v) => set("instructors", upd(instructors, i, { image: v }))} testid={`cms-about-instructor-image-${i}`} />
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- PROGRAMS --
export function ProgramsEditor({ value, onChange, title, onTitleChange }) {
  const programs = value?.programs || [];
  const setPrograms = (next) => onChange({ ...value, programs: next });
  const update = (i, patch) => setPrograms(programs.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const add = () => setPrograms([...programs, { name: "New Program", desc: "" }]);
  return (
    <div className="space-y-5">
      <Field label="Page Title">
        <input className="input" value={title} onChange={(e) => onTitleChange(e.target.value)} data-testid="cms-title" />
      </Field>
      <ArrayHeader label="Programs" count={programs.length} onAdd={add} />
      <SortableList
        items={programs}
        onChange={setPrograms}
        emptyText="No programs yet. Click Add."
        renderRow={(p, i) => (
          <>
            <input className="input" value={p.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Program name" data-testid={`cms-program-name-${i}`} />
            <RichTextEditor value={p.desc || ""} onChange={(v) => update(i, { desc: v })} testid={`cms-program-desc-${i}`} minHeight={120} />
          </>
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------- SCHEDULE --
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function ScheduleEditor({ value, onChange, title, onTitleChange }) {
  const classes = value?.classes || [];
  const setClasses = (next) => onChange({ ...value, classes: next });
  const update = (i, patch) => setClasses(classes.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const add = () => setClasses([...classes, { day: "Monday", time: "6:00 PM – 7:00 PM", class: "New Class" }]);
  return (
    <div className="space-y-5">
      <Field label="Page Title">
        <input className="input" value={title} onChange={(e) => onTitleChange(e.target.value)} data-testid="cms-title" />
      </Field>
      <ArrayHeader label="Classes" count={classes.length} onAdd={add} />
      <SortableList
        items={classes}
        onChange={setClasses}
        emptyText="No classes yet. Click Add."
        renderRow={(c, i) => (
          <div className="grid md:grid-cols-3 gap-3">
            <select className="input" value={c.day} onChange={(e) => update(i, { day: e.target.value })} data-testid={`cms-sch-day-${i}`}>
              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <input className="input" value={c.time} onChange={(e) => update(i, { time: e.target.value })} placeholder="6:00 PM – 7:00 PM" data-testid={`cms-sch-time-${i}`} />
            <input className="input" value={c.class} onChange={(e) => update(i, { class: e.target.value })} placeholder="Class name" data-testid={`cms-sch-class-${i}`} />
          </div>
        )}
      />
    </div>
  );
}

// -------------------------------------------------------------------- NEWS --
export function NewsEditor({ value, onChange, title, onTitleChange }) {
  const posts = value?.posts || [];
  const setPosts = (next) => onChange({ ...value, posts: next });
  const update = (i, patch) => setPosts(posts.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const add = () => setPosts([{ date: new Date().toISOString().slice(0, 10), title: "New Post", body: "" }, ...posts]);
  return (
    <div className="space-y-5">
      <Field label="Page Title">
        <input className="input" value={title} onChange={(e) => onTitleChange(e.target.value)} data-testid="cms-title" />
      </Field>
      <ArrayHeader label="Posts" count={posts.length} onAdd={add} />
      <SortableList
        items={posts}
        onChange={setPosts}
        emptyText="No posts yet. Click Add."
        renderRow={(p, i) => (
          <>
            <div className="grid md:grid-cols-[160px_1fr] gap-3">
              <input type="date" className="input" value={p.date || ""} onChange={(e) => update(i, { date: e.target.value })} data-testid={`cms-news-date-${i}`} />
              <input className="input" value={p.title} onChange={(e) => update(i, { title: e.target.value })} placeholder="Post title" data-testid={`cms-news-title-${i}`} />
            </div>
            <RichTextEditor value={p.body || ""} onChange={(v) => update(i, { body: v })} testid={`cms-news-body-${i}`} minHeight={140} />
          </>
        )}
      />
    </div>
  );
}

// ----------------------------------------------------------------- CONTACT --
export function ContactEditor({ value, onChange, title, onTitleChange }) {
  const c = value || {};
  const set = (k, v) => onChange({ ...c, [k]: v });
  const locations = c.locations || [];
  const upd = (i, patch) => set("locations", locations.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  return (
    <div className="space-y-6">
      <Field label="Page Title">
        <input className="input" value={title} onChange={(e) => onTitleChange(e.target.value)} data-testid="cms-title" />
      </Field>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Eyebrow"><input className="input" value={c.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} /></Field>
        <Field label="Heading"><input className="input" value={c.heading || ""} onChange={(e) => set("heading", e.target.value)} data-testid="cms-contact-heading" /></Field>
      </div>
      <Field label="Intro"><textarea className="input" rows={2} value={c.intro || ""} onChange={(e) => set("intro", e.target.value)} /></Field>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Email"><input className="input" value={c.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="cms-contact-email" /></Field>
        <Field label="Hours"><input className="input" value={c.hours || ""} onChange={(e) => set("hours", e.target.value)} data-testid="cms-contact-hours" /></Field>
      </div>
      <div>
        <ArrayHeader label="Locations" count={locations.length} onAdd={() => set("locations", [...locations, { name: "New Location", address: "", phone: "" }])} />
        <SortableList
          items={locations}
          onChange={(next) => set("locations", next)}
          emptyText="No locations yet. Click Add."
          renderRow={(l, i) => (
            <>
              <input className="input" value={l.name || ""} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Location name" data-testid={`cms-location-name-${i}`} />
              <div className="grid md:grid-cols-2 gap-3">
                <input className="input" value={l.address || ""} onChange={(e) => upd(i, { address: e.target.value })} placeholder="Address" />
                <input className="input" value={l.phone || ""} onChange={(e) => upd(i, { phone: e.target.value })} placeholder="Phone" />
              </div>
            </>
          )}
        />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- ID CARD --
// Customize the member certificate. Editable by admin AND super_admin.
export function IDCardEditor({ value, onChange, title, onTitleChange }) {
  const c = value || {};
  const set = (k, v) => onChange({ ...c, [k]: v });
  const handleImage = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      alert("Image must be under 1.5 MB. Please compress it first.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set(key, reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="space-y-5">
      <Field label="Page Title (admin label)">
        <input className="input" value={title} onChange={(e) => onTitleChange(e.target.value)} data-testid="cms-title" />
      </Field>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Dojo Name (top of card)">
          <input className="input" value={c.dojo_name || ""} onChange={(e) => set("dojo_name", e.target.value)} data-testid="idcard-dojo-name" />
        </Field>
        <Field label="Certificate Title">
          <input className="input" value={c.certificate_title || ""} onChange={(e) => set("certificate_title", e.target.value)} data-testid="idcard-cert-title" />
        </Field>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Kanji (top right)" hint="Japanese characters above the divider.">
          <input className="input" value={c.kanji_top || ""} onChange={(e) => set("kanji_top", e.target.value)} data-testid="idcard-kanji-top" />
        </Field>
        <Field label="Kanji (bottom right)">
          <input className="input" value={c.kanji_bottom || ""} onChange={(e) => set("kanji_bottom", e.target.value)} data-testid="idcard-kanji-bottom" />
        </Field>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Issued footer text">
          <input className="input" value={c.issued_text || ""} onChange={(e) => set("issued_text", e.target.value)} data-testid="idcard-issued" />
        </Field>
        <Field label="Scan caption">
          <input className="input" value={c.scan_text || ""} onChange={(e) => set("scan_text", e.target.value)} data-testid="idcard-scan-text" />
        </Field>
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        <Field label="Member label"><input className="input" value={c.name_label || ""} onChange={(e) => set("name_label", e.target.value)} /></Field>
        <Field label="Role label"><input className="input" value={c.role_label || ""} onChange={(e) => set("role_label", e.target.value)} /></Field>
        <Field label="Rank label"><input className="input" value={c.rank_label || ""} onChange={(e) => set("rank_label", e.target.value)} /></Field>
        <Field label="Member No. label"><input className="input" value={c.footer_label || ""} onChange={(e) => set("footer_label", e.target.value)} /></Field>
      </div>
      <Field label="Accent color (kanji + dot)" hint="Hex color, e.g. #7A0F17 for dojo maroon.">
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={c.accent_color || "#7A0F17"}
            onChange={(e) => set("accent_color", e.target.value)}
            className="h-10 w-16 border border-[var(--dojo-border)] cursor-pointer"
            data-testid="idcard-accent"
          />
          <input
            className="input"
            value={c.accent_color || ""}
            onChange={(e) => set("accent_color", e.target.value)}
            placeholder="#7A0F17"
          />
        </div>
      </Field>
      <Field label="Logo image" hint="Replace the dojo logo. Upload a PNG/JPG (under 1.5 MB). Leave empty to use default.">
        <div className="flex items-center gap-4">
          {c.logo_url && <img src={c.logo_url} alt="Logo preview" className="h-16 w-16 object-contain border border-[var(--dojo-border)]" />}
          <input type="file" accept="image/*" onChange={handleImage("logo_url")} className="text-sm" data-testid="idcard-logo-upload" />
          {c.logo_url && (
            <button type="button" onClick={() => set("logo_url", "")} className="text-xs text-[var(--dojo-hinomaru)] underline">Remove</button>
          )}
        </div>
      </Field>
      <Field label="Background image" hint="Optional watermark behind the card content. Subtle works best (under 1.5 MB).">
        <div className="flex items-center gap-4">
          {c.background_url && <img src={c.background_url} alt="Background preview" className="h-16 w-24 object-cover border border-[var(--dojo-border)]" />}
          <input type="file" accept="image/*" onChange={handleImage("background_url")} className="text-sm" data-testid="idcard-bg-upload" />
          {c.background_url && (
            <button type="button" onClick={() => set("background_url", "")} className="text-xs text-[var(--dojo-hinomaru)] underline">Remove</button>
          )}
        </div>
      </Field>
    </div>
  );
}

const EDITORS = {
  home: HomeEditor,
  about: AboutEditor,
  programs: ProgramsEditor,
  schedule: ScheduleEditor,
  news: NewsEditor,
  contact: ContactEditor,
  idcard: IDCardEditor,
};

export function getEditorForSlug(slug) {
  return EDITORS[slug] || null;
}
